import { useState, useEffect, useCallback } from 'react';
import { superAdminApi } from '../../api';
import { toast } from 'sonner';
import {
  Plus, Search, Users, MoreVertical, KeyRound, Loader2, Trash2, Edit2,
  Building2, Palette, Globe, Copy, Eye, EyeOff,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const APP_ORIGIN = window.location.origin;

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

export default function SAVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [resetVendor, setResetVendor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.getVendors();
      setVendors(data || []);
    } catch (e) {
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return [v.name, v.email, v.vendor_key, v.app_name, v.login_slug]
      .some(s => (s || '').toLowerCase().includes(q));
  });

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try {
      await superAdminApi.deleteVendor(v.id);
      toast.success('Vendor deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const copyLoginUrl = (slug) => {
    const url = `${APP_ORIGIN}/vendor/${slug}/login`;
    navigator.clipboard.writeText(url);
    toast.success('Login URL copied');
  };

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="sa-vendors-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Vendors</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Vendors are resellers — they create and manage their own clients, see only their data, and can white-label the app.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2" data-testid="create-vendor-btn">
          <Plus size={16} /> Create Vendor
        </Button>
      </header>

      <div className="flex items-center gap-2 max-w-sm">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, key, slug…"
            className="pl-9 h-9 text-sm"
            data-testid="vendors-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground">
            <Users size={36} className="mx-auto mb-3 text-muted-foreground/50" />
            {vendors.length === 0
              ? 'No vendors yet. Create the first one above.'
              : 'No vendors match your search.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(v => (
            <Card key={v.id} data-testid={`vendor-card-${v.id}`} className="overflow-hidden">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0"
                      style={{ backgroundColor: v.primary_color || '#3b82f6' }}
                    >
                      {(v.app_name || v.name)[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-foreground truncate">{v.name}</h3>
                      <p className="text-xs text-muted-foreground truncate">{v.app_name}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" data-testid={`vendor-menu-${v.id}`}>
                        <MoreVertical size={14} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditVendor(v)} className="gap-2">
                        <Edit2 size={13} /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setResetVendor(v)} className="gap-2">
                        <KeyRound size={13} /> Reset admin password
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(v)}
                        className="gap-2 text-destructive focus:text-destructive"
                      >
                        <Trash2 size={13} /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="space-y-1.5 text-xs">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <KeyRound size={11} /> <code className="text-foreground font-mono">{v.vendor_key}</code>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Building2 size={11} /> {v.client_count ?? 0} client{v.client_count === 1 ? '' : 's'}
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground truncate">
                    <Globe size={11} className="shrink-0" />
                    <span className="truncate">
                      <code className="font-mono">/vendor/{v.login_slug}/login</code>
                    </span>
                    <button
                      onClick={() => copyLoginUrl(v.login_slug)}
                      className="text-muted-foreground hover:text-foreground p-0.5"
                      title="Copy login URL"
                      data-testid={`copy-login-url-${v.id}`}
                    >
                      <Copy size={11} />
                    </button>
                  </div>
                  <div className="text-muted-foreground">Created {fmtDate(v.created_at)}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateVendorDialog open={showCreate} onClose={() => setShowCreate(false)} onSaved={load} />
      <EditVendorDialog vendor={editVendor} onClose={() => setEditVendor(null)} onSaved={load} />
      <ResetPasswordDialog vendor={resetVendor} onClose={() => setResetVendor(null)} />
    </div>
  );
}

// ─── Dialogs ─────────────────────────────────────────────────────────────────

function CreateVendorDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', email: '', app_name: '', login_slug: '', primary_color: '#3b82f6',
    admin_name: '', admin_email: '', admin_password: '',
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (!open) setForm({
      name: '', email: '', app_name: '', login_slug: '', primary_color: '#3b82f6',
      admin_name: '', admin_email: '', admin_password: '',
    });
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error('Name and email required');
    if (!form.admin_email.trim() || !form.admin_password.trim()) return toast.error('Admin email + password required');
    setSaving(true);
    try {
      const payload = {
        ...form,
        app_name: form.app_name.trim() || 'CredBloom',
        login_slug: form.login_slug.trim() || undefined,
      };
      const { data } = await superAdminApi.createVendor(payload);
      toast.success(`Vendor created — key: ${data.vendor_key}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-lg max-h-[calc(100vh-1rem)] sm:max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="create-vendor-dialog">
        <DialogHeader className="px-5 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle>Create Vendor</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vendor</h3>
              <div className="space-y-1.5">
                <Label className="text-xs">Company Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="vendor-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Contact Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
            </section>
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Palette size={11} /> White-Label Branding
              </h3>
              <div className="space-y-1.5">
                <Label className="text-xs">App Name (shown to their clients)</Label>
                <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="e.g. Acme Reviews" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Login Slug</Label>
                  <Input
                    value={form.login_slug}
                    onChange={(e) => setForm({ ...form, login_slug: e.target.value.toLowerCase() })}
                    placeholder="acme"
                    data-testid="vendor-slug-input"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Their clients log in at <code className="font-mono">/vendor/{form.login_slug || '<slug>'}/login</code>
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Primary Color</Label>
                  <Input
                    type="color"
                    value={form.primary_color}
                    onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                    className="h-9 w-16 p-1"
                  />
                </div>
              </div>
            </section>
            <section className="space-y-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vendor Admin Login</h3>
              <div className="space-y-1.5">
                <Label className="text-xs">Admin Name *</Label>
                <Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Admin Email *</Label>
                <Input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Admin Password *</Label>
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    value={form.admin_password}
                    onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                    required
                    className="pr-9"
                  />
                  <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            </section>
          </div>
          <DialogFooter className="px-5 sm:px-6 py-3 sm:py-4 border-t border-border shrink-0 gap-2 bg-background">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 gap-2" data-testid="save-vendor-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Create Vendor
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditVendorDialog({ vendor, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', email: '', app_name: '', login_slug: '', primary_color: '#3b82f6' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (vendor) setForm({
      name: vendor.name || '',
      email: vendor.email || '',
      app_name: vendor.app_name || '',
      login_slug: vendor.login_slug || '',
      primary_color: vendor.primary_color || '#3b82f6',
    });
  }, [vendor]);

  if (!vendor) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await superAdminApi.updateVendor(vendor.id, form);
      toast.success('Vendor updated');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update vendor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="edit-vendor-dialog">
        <DialogHeader>
          <DialogTitle>Edit Vendor</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">App Name</Label>
            <Input value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Login Slug</Label>
            <Input value={form.login_slug} onChange={(e) => setForm({ ...form, login_slug: e.target.value.toLowerCase() })} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Primary Color</Label>
            <Input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="h-9 w-16 p-1" /></div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ vendor, onClose }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!vendor) setPw(''); }, [vendor]);

  if (!vendor) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error('Password must be ≥ 8 chars');
    setSaving(true);
    try {
      await superAdminApi.resetVendorPassword(vendor.id, { new_password: pw });
      toast.success('Password reset');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Reset Admin Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Reset password for <strong>{vendor.admin_user?.email || vendor.name}</strong>
          </p>
          <Input type="text" placeholder="New password (≥ 8 chars)" value={pw} onChange={(e) => setPw(e.target.value)} />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
              Reset
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
